/* AMBIENT OCCLUSION, FOR EVERY OBJECT AT ONCE.

   Screen-space, so it needs nothing from the objects themselves: a recess, 3D
   Text and an imported model all darken the same way, in their own creases and
   wherever one of them comes close to another — text standing in a recess
   shades the wall behind it, which no per-object bake could do.

   It is applied ON TOP of the finished frame rather than folded into the
   colour render. The picture renderer.render() draws — tone mapping, colour
   space, blend modes, everything the export already gets right — is left
   exactly as it was, and this only multiplies it darker where the occlusion
   says so. At strength 0 none of it runs at all, so a project that never
   touches the setting renders bit-for-bit as before.

   Three passes, each into its own target:

     1. G-buffer: depth plus view-space normal, drawn from the user's objects
        alone. Helpers, bezels, the calibration grid and the lamp gizmos are
        hidden for it, so a workspace aid never shades the deliverable.
     2. GTAO (three.js's own shader) reads that and writes the occlusion.
     3. Poisson denoise (also three.js's) smooths its sampling noise.

   The G-buffer's ALPHA carries one more fact: whether a pixel may be darkened.
   Lit surfaces write 1. Flat-colour ("unlit") objects write 0 — they still
   block, so they shade what is around them, but the hex picked for them is the
   hex that lands in the export, and darkening it would break that promise.
   Anything see-through writes 0 over whatever is behind it without blocking,
   so a PNG's transparent corners neither cast a rectangle of shadow nor let
   the wall behind them go dark through a hole that is meant to be clear.

   Uses three.js's GTAOShader and PoissonDenoiseShader directly rather than
   GTAOPass: the pass cannot take an externally drawn G-buffer (its setGBuffer
   reads a render target it only creates when it draws its own), and drawing
   its own means drawing every mesh in the scene, helpers included. */
import * as THREE from 'three';
import { FullScreenQuad } from 'three/examples/jsm/postprocessing/Pass.js';
import { GTAOShader, generateMagicSquareNoise } from 'three/examples/jsm/shaders/GTAOShader.js';
import { PoissonDenoiseShader, generatePdSamplePointInitializer } from 'three/examples/jsm/shaders/PoissonDenoiseShader.js';
import { SimplexNoise } from 'three/examples/jsm/math/SimplexNoise.js';
import { content, renderer, scene } from './context.js';
import { S, clamp } from '../state/store.js';

let T = null;   // everything GPU-side, built on first use — most projects never need it

function pdNoise(size = 64){
  const simplex = new SimplexNoise();
  const data = new Uint8Array(size * size * 4);
  for(let i = 0; i < size; i++) for(let j = 0; j < size; j++){
    const k = (i * size + j) * 4;
    data[k]     = (simplex.noise(i, j) * .5 + .5) * 255;
    data[k + 1] = (simplex.noise(i + size, j) * .5 + .5) * 255;
    data[k + 2] = (simplex.noise(i, j + size) * .5 + .5) * 255;
    data[k + 3] = (simplex.noise(i + size, j + size) * .5 + .5) * 255;
  }
  const t = new THREE.DataTexture(data, size, size, THREE.RGBAFormat, THREE.UnsignedByteType);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.needsUpdate = true;
  return t;
}

function build(){
  const depth = new THREE.DepthTexture();
  depth.format = THREE.DepthStencilFormat;
  depth.type = THREE.UnsignedInt248Type;
  const gbuf = new THREE.WebGLRenderTarget(1, 1, {
    minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter,
    type: THREE.HalfFloatType, depthTexture: depth
  });
  const aoRT = new THREE.WebGLRenderTarget(1, 1, { type: THREE.HalfFloatType });
  const pdRT = aoRT.clone();

  /* Two changes to three's shader, both about samples that should not count
     at full weight.

     Past the frame's edge a sample now reads as empty space instead of as the
     clamped edge pixel — the deliverable has nothing out there to occlude with.

     And the depth cut-off fades instead of snapping. A screen-space pass only
     sees what the camera sees: looking up into a recess, its floor is hidden
     behind the front lip, so a sample aimed at the floor lands on the lip
     instead. Stock GTAO counts that lip in full when it is within Thickness in
     depth and ignores it entirely one step further, and on a recess wall that
     cut-off drew a hard vertical line one Reach in from the mouth — deep inside
     no floor shading at all, near the front all of it. Faded over twice the
     distance, the same change reads as the soft falloff toward the opening it
     physically is. */
  const SRC = GTAOShader.fragmentShader;
  const OOB = 'float sampleSceneDepth = getDepth(sampleUv);';
  const CUT = 'if (abs(viewDelta.z) < thickness) {';
  const ADD = 'distanceFallOff));';
  if(!SRC.includes(OOB) || SRC.split(CUT).length !== 3 || SRC.split(ADD).length !== 3)
    throw new Error('GTAOShader changed: the patches in scene/ao.js no longer apply');
  const gtao = new THREE.ShaderMaterial({
    /* 24 samples is 3 directions of 8 steps. The stock 16 (3 of 6) left a
       large Reach stepping visibly — rings around a model instead of a halo. */
    defines: { ...GTAOShader.defines, NORMAL_VECTOR_TYPE: 1, DEPTH_SWIZZLING: 'x', PERSPECTIVE_CAMERA: 1, SAMPLES: 24 },
    uniforms: THREE.UniformsUtils.clone(GTAOShader.uniforms),
    vertexShader: GTAOShader.vertexShader,
    fragmentShader: SRC
      .replace(OOB, 'float sampleSceneDepth = (any(lessThan(sampleUv, vec2(0.))) || any(greaterThan(sampleUv, vec2(1.)))) ? 1.0 : getDepth(sampleUv);')
      .replaceAll(CUT, 'if (abs(viewDelta.z) < thickness * 2.) {')
      .replaceAll(ADD, 'distanceFallOff) * (1. - smoothstep(thickness * .5, thickness * 2., abs(viewDelta.z))));'),
    blending: THREE.NoBlending, depthTest: false, depthWrite: false
  });
  gtao.uniforms.tNormal.value = gbuf.texture;
  gtao.uniforms.tDepth.value = depth;
  gtao.uniforms.tNoise.value = generateMagicSquareNoise();

  const pd = new THREE.ShaderMaterial({
    defines: { ...PoissonDenoiseShader.defines, NORMAL_VECTOR_TYPE: 1,
               SAMPLES: 16, SAMPLE_VECTORS: generatePdSamplePointInitializer(16, 2, 2) },
    uniforms: THREE.UniformsUtils.clone(PoissonDenoiseShader.uniforms),
    vertexShader: PoissonDenoiseShader.vertexShader, fragmentShader: PoissonDenoiseShader.fragmentShader,
    blending: THREE.NoBlending, depthTest: false, depthWrite: false
  });
  pd.uniforms.tDiffuse.value = aoRT.texture;
  pd.uniforms.tNormal.value = gbuf.texture;
  pd.uniforms.tDepth.value = depth;
  pd.uniforms.tNoise.value = pdNoise();
  pd.uniforms.lumaPhi.value = 10;
  pd.uniforms.depthPhi.value = 2;
  pd.uniforms.normalPhi.value = 3;

  /* dst.rgb *= factor, dst.a untouched. Multiplying premultiplied colour by a
     scalar keeps it validly premultiplied, and leaving alpha alone is what
     keeps a transparent export's empty background exactly as empty. */
  const apply = new THREE.ShaderMaterial({
    uniforms: { tAO: { value: pdRT.texture }, tG: { value: gbuf.texture }, strength: { value: 1 } },
    vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
    fragmentShader: `
      uniform sampler2D tAO; uniform sampler2D tG; uniform float strength; varying vec2 vUv;
      void main(){
        float ao = texture2D(tAO, vUv).r;
        float receives = texture2D(tG, vUv).a;
        gl_FragColor = vec4(vec3(mix(1.0, ao, strength * receives)), 1.0);
      }`,
    transparent: true, depthTest: false, depthWrite: false,
    blending: THREE.CustomBlending, blendEquation: THREE.AddEquation,
    blendSrc: THREE.ZeroFactor, blendDst: THREE.SrcColorFactor,
    blendSrcAlpha: THREE.ZeroFactor, blendDstAlpha: THREE.OneFactor
  });

  T = { gbuf, aoRT, pdRT, gtao, pd, apply, quad: new FullScreenQuad(null),
        normals: new Map(), masks: new WeakMap(), protects: new WeakMap() };
}

/* The G-buffer material for one of the scene's own materials, or null to leave
   that mesh out of the pass entirely. Cached, because a material switch per
   mesh per frame is cheap and a program compile per mesh per frame is not. */
function normalMat(side, receives, clip){
  const key = side + '|' + receives + '|' + (clip ? 1 : 0);
  let m = T.normals.get(key);
  if(!m){
    m = new THREE.MeshNormalMaterial({ side, blending: THREE.NoBlending });
    m.opacity = receives ? 1 : 0;       // NoBlending writes this straight into alpha
    T.normals.set(key, m);
  }
  m.clippingPlanes = clip;              // a recess clipped to the screen's cone stays clipped here
  return m;
}
function maskMat(src){
  let m = T.masks.get(src);
  if(!m){
    /* Writes 0 everywhere its fragment survives the alpha test: "do not
       darken this pixel". It writes depth there too, but only there — the
       alpha test cuts the fragments to the picture's solid part, so a PNG
       cut-out blocks as its silhouette, never as a rectangle. Depth of field
       (scene/dof.js) needs that depth: without it a cut-out would be blurred
       as though it were whatever stands behind it. */
    m = new THREE.MeshBasicMaterial({
      transparent: true, depthWrite: true,
      blending: THREE.CustomBlending, blendEquation: THREE.AddEquation,
      blendSrc: THREE.ZeroFactor, blendDst: THREE.ZeroFactor,
      blendSrcAlpha: THREE.ZeroFactor, blendDstAlpha: THREE.ZeroFactor
    });
    T.masks.set(src, m);
  }
  m.map = src.map || null;
  m.alphaMap = src.alphaMap || null;
  m.alphaTest = Math.max(.5, src.alphaTest || 0);
  m.side = src.side;
  m.clippingPlanes = src.clippingPlanes;
  return m;
}
function gbufferMat(m){
  if(!m || m.visible === false) return null;
  if(m.isLineMaterial || m.isShaderMaterial || m.isPointsMaterial || m.isSpriteMaterial) return null;
  if(m.transparent || m.opacity < 1 || m.alphaTest > 0 || m.alphaMap) return maskMat(m);
  return normalMat(m.side, !m.isMeshBasicMaterial, m.clippingPlanes || null);
}

/* For an object that does not cast: leave the colour and depth channels exactly as
   they are and write ZERO into alpha, which is "do not darken this pixel".

   Depth-TESTED but not depth-written, against the depth the casters left behind.
   That is the whole trick: where the object is in front of every caster it zeroes
   the pixel, so the wall seen past it is not shaded onto it; where a caster is in
   front of it the test fails and the caster's own pixel is left alone. And because
   it writes no depth, it is not there for the occlusion pass to be blocked by. */
function protectMat(src){
  if(!src || src.visible === false) return null;
  if(src.isLineMaterial || src.isShaderMaterial || src.isPointsMaterial || src.isSpriteMaterial) return null;
  let m = T.protects.get(src);
  if(!m){
    m = new THREE.MeshBasicMaterial({
      transparent: true, depthWrite: false,
      blending: THREE.CustomBlending, blendEquation: THREE.AddEquation,
      blendSrc: THREE.ZeroFactor, blendDst: THREE.OneFactor,            // colour: unchanged
      blendSrcAlpha: THREE.ZeroFactor, blendDstAlpha: THREE.ZeroFactor  // alpha: 0
    });
    T.protects.set(src, m);
  }
  // a cut-out only protects its solid part, the same alpha test the blockers use
  m.map = src.map || null;
  m.alphaMap = src.alphaMap || null;
  m.alphaTest = Math.max(.5, src.alphaTest || 0);
  m.side = src.side;
  m.clippingPlanes = src.clippingPlanes;
  return m;
}

const _swapped = [], _hidden = [];
const _size = new THREE.Vector2(), _clear = new THREE.Color();

/**
 * @param occludersOnly  leave out every object that does not cast a shadow, as
 *   ambient occlusion needs, and mark its pixels "do not darken". Depth of field
 *   asks for everything instead: it blurs by how far away the picture is, and an
 *   object it could not see would be blurred as though it were the wall behind it.
 */
function drawGBuffer(cam, occludersOnly = false){
  const passive = [];                    // meshes that do not cast, when only casters count
  content.traverse(n => {
    if(!n.isMesh || !n.material) return;
    const src = n.material;
    const skip = occludersOnly && n.castShadow === false;
    if(skip) passive.push(n);
    _swapped.push(n, src);
    n.material = skip
      ? (Array.isArray(src) ? src.map(() => HIDE) : HIDE)
      : (Array.isArray(src) ? src.map(m => gbufferMat(m) || HIDE) : (gbufferMat(src) || HIDE));
  });
  for(const c of scene.children){
    if(c !== content && c.visible){ c.visible = false; _hidden.push(c); }
  }
  const bg = scene.background, auto = renderer.autoClear, shadows = renderer.shadowMap.autoUpdate;
  scene.background = null;
  renderer.shadowMap.autoUpdate = false;     // the colour pass already drew them this frame
  renderer.autoClear = false;
  renderer.setRenderTarget(T.gbuf);
  renderer.setClearColor(0x000000, 0);
  renderer.clear(true, true, true);
  try {
    renderer.render(scene, cam);
    if(passive.length){
      /* A second pass, over the same depth: the casters are hidden and the others
         write "do not darken" where they are in front. See protectMat. */
      const orig = new Map();
      for(let i = 0; i < _swapped.length; i += 2) orig.set(_swapped[i], _swapped[i + 1]);
      content.traverse(n => {
        if(!n.isMesh || !orig.has(n)) return;
        const src = orig.get(n);
        n.material = passive.includes(n)
          ? (Array.isArray(src) ? src.map(m => protectMat(m) || HIDE) : (protectMat(src) || HIDE))
          : (Array.isArray(src) ? src.map(() => HIDE) : HIDE);
      });
      renderer.render(scene, cam);
    }
  }
  finally {
    renderer.autoClear = auto;
    renderer.shadowMap.autoUpdate = shadows;
    scene.background = bg;
    while(_hidden.length) _hidden.pop().visible = true;
    for(let i = 0; i < _swapped.length; i += 2) _swapped[i].material = _swapped[i + 1];
    _swapped.length = 0;
  }
}
/* A mesh with nothing to contribute still has to be given SOME material while
   the others are swapped; this one draws nothing. */
const HIDE = new THREE.MeshBasicMaterial({ visible: false });

/* The buffers follow the drawing buffer's size, which changes with the view,
   the window and an export's supersampling. */
function fitTargets(){
  renderer.getDrawingBufferSize(_size);
  const w = Math.max(1, _size.x | 0), h = Math.max(1, _size.y | 0);
  if(T.gbuf.width !== w || T.gbuf.height !== h){
    T.gbuf.setSize(w, h); T.aoRT.setSize(w, h); T.pdRT.setSize(w, h);
  }
  return { w, h };
}

/**
 * The depth of the user's objects as seen through `cam`, drawn now — the same
 * pass occlusion reads, for depth of field (scene/dof.js) to read as well.
 * Leaves the renderer targeting what it was targeting.
 */
export function drawDepth(cam){
  if(!T) build();
  const target = renderer.getRenderTarget();
  renderer.getClearColor(_clear);
  const clearA = renderer.getClearAlpha();
  fitTargets();
  drawGBuffer(cam);
  renderer.setRenderTarget(target);
  renderer.setClearColor(_clear, clearA);
  return T.gbuf.depthTexture;
}

function fullscreen(mat, target){
  renderer.setRenderTarget(target);
  T.quad.material = mat;
  T.quad.render(renderer);
}

/**
 * Darken the frame renderer.render(scene, cam) just left in the canvas.
 * A no-op at strength 0, so callers do not need to ask first.
 */
export function applyAO(cam){
  const strength = clamp(+S.aoStrength || 0, 0, 1);
  if(strength <= 0) return;
  if(!T) build();

  const target = renderer.getRenderTarget();
  const { w, h } = fitTargets();
  renderer.getClearColor(_clear);
  const clearA = renderer.getClearAlpha();

  drawGBuffer(cam, true);        // only what casts a shadow occludes: an object with Shadow off must not shade its neighbours

  /* Reach is set against the screen's shorter side, so the same setting looks
     the same on a 1 m sign and a 20 m wall — in metres it would need resetting
     every time the screen size changed. */
  const radius = clamp(+S.aoReach || .15, .01, 2) * Math.min(S.screen.w, S.screen.h);
  const u = T.gtao.uniforms;
  u.radius.value = radius;
  u.thickness.value = radius;
  u.distanceExponent.value = 1;
  u.distanceFallOff.value = 1;
  u.scale.value = 1;
  u.resolution.value.set(w, h);
  u.cameraNear.value = cam.near;
  u.cameraFar.value = cam.far;
  u.cameraProjectionMatrix.value.copy(cam.projectionMatrix);
  u.cameraProjectionMatrixInverse.value.copy(cam.projectionMatrixInverse);
  u.cameraWorldMatrix.value.copy(cam.matrixWorld);
  renderer.setClearColor(0xffffff, 1);     // GTAO discards the background: unoccluded
  renderer.setRenderTarget(T.aoRT); renderer.clear(true, false, false);
  fullscreen(T.gtao, T.aoRT);

  const p = T.pd.uniforms;
  p.resolution.value.set(w, h);
  p.cameraProjectionMatrixInverse.value.copy(cam.projectionMatrixInverse);
  /* The denoiser's reach is in pixels, so it has to grow with the buffer or a
     supersampled export comes out noisier than the preview of the same frame. */
  p.radius.value = 8 * Math.max(1, h / 1080);
  renderer.setRenderTarget(T.pdRT); renderer.clear(true, false, false);
  fullscreen(T.pd, T.pdRT);

  renderer.setClearColor(_clear, clearA);
  T.apply.uniforms.strength.value = strength;
  const auto = renderer.autoClear;
  renderer.autoClear = false;
  fullscreen(T.apply, target);
  renderer.autoClear = auto;
}
